// [檔案： src/js/widget.js]

import { registerPlugin, Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import * as state from './state.js';
import { storageService } from './storage.js';
import { t, currentLang } from './i18n.js';

// 註冊原生 Widget 橋接外掛
export const WidgetBridge = registerPlugin('WidgetBridge');

// 支援的 9 種語言桌面小組件本地化辭典
const WIDGET_I18N_DICTIONARY = {
    'zh-TW': {
        overview: '本月財務概覽',
        balance: '本月餘額',
        income: '總收入',
        expense: '總支出',
        incomeShort: '收',
        expenseShort: '支',
        add: '記一筆',
        food: '餐飲',
        transport: '交通'
    },
    'zh-CN': {
        overview: '本月财务概览',
        balance: '本月余额',
        income: '总收入',
        expense: '总支出',
        incomeShort: '收',
        expenseShort: '支',
        add: '记一笔',
        food: '餐饮',
        transport: '交通'
    },
    'en': {
        overview: 'Overview',
        balance: 'Monthly Balance',
        income: 'Total Income',
        expense: 'Total Expense',
        incomeShort: 'In',
        expenseShort: 'Ex',
        add: 'Add',
        food: 'Food',
        transport: 'Transport'
    },
    'ja': {
        overview: '今月の財務概要',
        balance: '今月の残高',
        income: '総収入',
        expense: '総支出',
        incomeShort: '収',
        expenseShort: '支',
        add: '記録',
        food: '食費',
        transport: '交通費'
    },
    'ko': {
        overview: '이달의 재정 개요',
        balance: '이번 달 잔액',
        income: '총 수입',
        expense: '총 지출',
        incomeShort: '수',
        expenseShort: '지',
        add: '기록',
        food: '식비',
        transport: '교통'
    },
    'de': {
        overview: 'Finanzübersicht',
        balance: 'Monatssaldo',
        income: 'Einnahmen',
        expense: 'Ausgaben',
        incomeShort: 'Ein',
        expenseShort: 'Aus',
        add: 'Buchen',
        food: 'Essen',
        transport: 'Transport'
    },
    'es': {
        overview: 'Resumen Mensual',
        balance: 'Saldo del Mes',
        income: 'Ingresos',
        expense: 'Gastos',
        incomeShort: 'Ing',
        expenseShort: 'Gas',
        add: 'Añadir',
        food: 'Comida',
        transport: 'Transporte'
    },
    'fr': {
        overview: 'Aperçu Financier',
        balance: 'Solde du Mois',
        income: 'Revenus',
        expense: 'Dépenses',
        incomeShort: 'Rev',
        expenseShort: 'Dép',
        add: 'Ajouter',
        food: 'Repas',
        transport: 'Transport'
    },
    'pt': {
        overview: 'Resumo Mensal',
        balance: 'Saldo Mensal',
        income: 'Receitas',
        expense: 'Despesas',
        incomeShort: 'Rec',
        expenseShort: 'Des',
        add: 'Adicionar',
        food: 'Refeição',
        transport: 'Transporte'
    }
};

/**
 * 根據當前設定的語言生成小組件多語文字 (完整涵蓋 9 種語言)
 */
function getWidgetLabels(lang) {
    const matched = WIDGET_I18N_DICTIONARY[lang] || WIDGET_I18N_DICTIONARY['en'];

    return {
        overview: matched.overview,
        balance: t('balance') || matched.balance,
        income: t('totalIncome') || matched.income,
        expense: t('totalExpense') || matched.expense,
        incomeShort: matched.incomeShort,
        expenseShort: matched.expenseShort,
        add: matched.add,
        food: t('cat_food') || matched.food,
        transport: t('cat_transport') || matched.transport
    };
}

// 監聽語言切換事件，自動同步小組件語言
if (typeof window !== 'undefined') {
    window.addEventListener('app:languageChanged', () => {
        console.log('🌐 [Widget JS] Language changed, resyncing widget snapshot...');
        syncWidgetSnapshot();
    });
}

/**
 * 計算並同步當前最新財務快照給原生桌面小組件
 */
export async function syncWidgetSnapshot() {
    if (!Capacitor.isNativePlatform()) {
        console.log('[Widget JS] Not native platform, skip widget sync.');
        return;
    }

    try {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const todayStr = `${year}-${month}-${day}`;

        // 查詢當月所有未刪除交易（不論當前畫面選在哪個月份）
        let transactions = [];
        try {
            transactions = await storageService.getTransactions(state.userId, now);
        } catch (dbErr) {
            console.warn('[Widget JS] Error querying transactions from db, fallback to state:', dbErr);
            transactions = state.localTransactions || [];
        }

        let totalIncome = 0;
        let totalExpense = 0;
        let todayExpense = 0;
        let todayIncome = 0;

        // 計算當月及當日收支
        transactions.forEach(tx => {
            if (!tx.date) return;
            const amt = Number(tx.amount) || 0;
            // 兼容 2026-09-03 或 2026/09/03
            const normalizedDate = tx.date.replace(/\//g, '-');
            const isToday = normalizedDate.startsWith(todayStr);

            if (tx.type === 'income') {
                totalIncome += amt;
                if (isToday) todayIncome += amt;
            } else {
                totalExpense += amt;
                if (isToday) todayExpense += amt;
            }
        });

        // 當月結餘 = 收入 - 支出
        const totalBalance = totalIncome - totalExpense;

        // 取最近 3 筆記錄
        const recentTransactions = transactions.slice(0, 3).map(tx => ({
            id: String(tx.id || ''),
            amount: Number(tx.amount) || 0,
            type: tx.type || 'expense',
            category: tx.category || 'cat_general',
            description: tx.description || '',
            date: tx.date || ''
        }));

        const widgetPayload = {
            totalBalance: parseFloat(totalBalance.toFixed(2)),
            totalIncome: parseFloat(totalIncome.toFixed(2)),
            totalExpense: parseFloat(totalExpense.toFixed(2)),
            todayExpense: parseFloat(todayExpense.toFixed(2)),
            todayIncome: parseFloat(todayIncome.toFixed(2)),
            recentTransactions,
            currency: '', // 不顯示貨幣符號
            lang: currentLang || 'zh-TW',
            labels: getWidgetLabels(currentLang || 'zh-TW'),
            updatedAt: Date.now()
        };

        console.log('🎯 [Widget JS] Sending snapshot to native bridge:', widgetPayload);
        const res = await WidgetBridge.setData({ data: widgetPayload });
        console.log('✅ [Widget JS] Native bridge response:', res);
    } catch (e) {
        console.error('❌ [Widget JS] Failed to sync widget snapshot:', e);
    }
}

/**
 * 初始化桌面小組件 Deep Link 喚醒監聽
 * 支援格式：lightflow://add?type=expense&category=cat_food
 */
export function initWidgetDeepLink(onOpenAddModal) {
    if (!Capacitor.isNativePlatform()) {
        return;
    }

    try {
        App.addListener('appUrlOpen', (event) => {
            console.log('🎯 [Widget DeepLink] App opened via URL:', event.url);
            if (!event.url) return;

            try {
                const url = new URL(event.url);
                const host = url.host; // 'add'
                const pathname = url.pathname;
                
                if (host === 'add' || pathname.includes('add')) {
                    const type = url.searchParams.get('type') || 'expense';
                    const category = url.searchParams.get('category') || 'cat_general';
                    
                    if (typeof onOpenAddModal === 'function') {
                        onOpenAddModal({ type, category });
                    }
                }
            } catch (err) {
                console.error('❌ [Widget DeepLink] Error parsing URL:', err);
            }
        });
    } catch (e) {
        console.warn('❌ [Widget DeepLink] Failed to init listener:', e);
    }
}
